import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, BookMarked, ChevronRight, Users, UserRound, Search } from "lucide-react";

export default function AdminProgramArchive() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/study-programs/all")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const filtered = rows.filter((r) => {
      const s = `${r.title} ${r.student_name} ${r.grade}`.toLowerCase();
      return s.includes(q.toLowerCase());
    });
    const map = {};
    filtered.forEach((r) => {
      const key = (r.title || "").trim() || "Başlıksız Program";
      (map[key] = map[key] || []).push(r);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0], "tr"));
  }, [rows, q]);

  return (
    <div className="mx-auto max-w-[1200px] space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-600"><FolderOpen className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Program Arşivi</h1>
            <p className="text-slate-500">Öğrencilere atanmış tüm çalışma programları — yayınevi (kaynak adı) halinde gruplanmış.</p>
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="archive-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Yayınevi / öğrenci ara…" className="w-72 rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-violet-400" />
        </div>
      </div>

      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-slate-200/60" />
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <BookMarked className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">Henüz atanmış çalışma programı yok</p>
          <p className="text-sm text-slate-500">Çalışma Programı Düzenle ekranından program oluşturun.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map(([title, items]) => (
            <div key={title} data-testid={`archive-group-${title}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between bg-[#1E293B] px-5 py-3.5 text-white">
                <h3 className="font-display text-lg font-bold tracking-wide">{title}</h3>
                <span className="rounded-full bg-violet-500 px-3 py-1 text-xs font-bold">{items.length} öğrenci</span>
              </div>
              <div className="divide-y divide-slate-100">
                {items.map((r) => (
                  <button
                    key={r.student_id}
                    data-testid={`archive-row-${r.student_id}`}
                    onClick={() => nav(`/admin/calisma-programi?student=${r.student_id}&name=${encodeURIComponent(r.student_name)}&program=${r.id}`)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition hover:bg-violet-50/60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><UserRound className="h-5 w-5" /></div>
                      <div>
                        <div className="font-bold text-slate-800">{r.student_name}</div>
                        <div className="text-xs text-slate-500">{r.grade}. Sınıf{r.section ? ` / ${r.section}` : ""}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">{r.week_count} hafta</span>
                      <ChevronRight className="h-5 w-5 text-slate-300" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
